import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import StatusBlock from '@/components/base/StatusBlock';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  getArticleById,
  getCategory,
  getRelatedArticles,
  formatDate,
  formatViews,
  TYPE_LABEL,
  readFavorites,
  writeFavorites,
} from '@/lib/content';

const SHARE_TARGETS = [
  { name: '微信', icon: 'ri-wechat-line' },
  { name: '微博', icon: 'ri-weibo-line' },
  { name: 'QQ', icon: 'ri-qq-line' },
];

export default function DetailPage() {
  const { id = '' } = useParams();
  const article = getArticleById(id);
  const category = article ? getCategory(article.category) : undefined;
  const [expanded, setExpanded] = useState(false);
  const [favorited, setFavorited] = useState(false);
  const [toast, setToast] = useState('');

  useDocumentTitle(
    article ? `${article.title} - 疯狂试爱2影视资讯` : '影视内容不存在 - 疯狂试爱2',
    article?.summary,
  );

  useEffect(() => {
    setExpanded(false);
    if (article) {
      setFavorited(readFavorites().includes(article.id));
    }
  }, [article]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(''), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const showToast = (message: string) => setToast(message);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast('链接已复制，快去分享给好友吧');
    } catch {
      showToast('复制失败，请手动复制地址栏链接');
    }
  };

  const toggleFavorite = () => {
    if (!article) return;
    const current = readFavorites();
    if (current.includes(article.id)) {
      writeFavorites(current.filter((item) => item !== article.id));
      setFavorited(false);
      showToast('已取消收藏');
    } else {
      writeFavorites([...current, article.id]);
      setFavorited(true);
      showToast('收藏成功，已保存到本地');
    }
  };

  if (!article) {
    return (
      <section className="mx-auto max-w-[1280px] px-4 py-20 md:px-8">
        <StatusBlock
          icon="ri-error-warning-line"
          title="该影视内容不存在"
          description="链接可能已失效或内容已下架，你可以返回首页继续浏览其他内容。"
        />
        <div className="mt-6 text-center">
          <Link
            to="/"
            className="inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </section>
    );
  }

  const paragraphs = article.fullText.split('\n\n');
  const relatedArticles = getRelatedArticles(article, 3);
  const collapsedText = paragraphs.slice(0, 2);

  return (
    <>
      <section className="bg-primary-900">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <div className="[&_a]:text-primary-200 [&_a:hover]:text-accent-400 [&_span]:text-background-50 [&_i]:text-primary-300">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: category?.name ?? '内容', to: `/category/${article.category}` },
                { label: article.subcategory },
              ]}
            />
          </div>
        </div>
      </section>

      <article className="mx-auto max-w-[1280px] px-4 py-10 md:px-8 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[360px_1fr] lg:gap-12">
          {/* Left column */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="h-56 w-full overflow-hidden rounded-lg bg-background-100 sm:h-72 lg:h-64">
              <img src={article.cover} alt={article.title} className="h-full w-full object-cover" />
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">作品信息</h2>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">类型</dt>
                  <dd className="text-foreground-800">{TYPE_LABEL[article.type]} · {article.subcategory}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">上映日期</dt>
                  <dd className="text-foreground-800">{formatDate(article.releaseDate)}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">导演</dt>
                  <dd className="text-foreground-800">{article.director}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">主要演员</dt>
                  <dd className="text-foreground-800">{article.cast.join('、')}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">热度</dt>
                  <dd className="flex items-center gap-1 text-foreground-800">
                    <i className="ri-fire-line text-accent-500"></i>
                    {formatViews(article.views)} 阅读
                  </dd>
                </div>
              </dl>

              <div className="mt-5 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="rounded bg-secondary-100 px-2 py-1 text-xs font-medium text-secondary-900">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-5 flex flex-col gap-3 border-t border-background-100 pt-5 sm:flex-row">
                <button
                  type="button"
                  onClick={toggleFavorite}
                  className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold transition-colors cursor-pointer ${
                    favorited
                      ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                      : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  }`}
                >
                  <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={copyLink}
                  className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
                >
                  <i className="ri-link"></i>
                  复制链接
                </button>
              </div>

              <div className="mt-4">
                <p className="mb-2 text-xs text-foreground-500">分享到</p>
                <div className="flex items-center gap-2">
                  {SHARE_TARGETS.map((target) => (
                    <button
                      key={target.name}
                      type="button"
                      aria-label={`分享到${target.name}`}
                      onClick={copyLink}
                      className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
                    >
                      <i className={`${target.icon} text-lg`}></i>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Right column */}
          <div className="min-w-0">
            <span className="rounded bg-accent-500 px-2 py-1 text-xs font-semibold text-background-50">
              {TYPE_LABEL[article.type]}
            </span>
            <h1 className="mt-3 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-3xl">
              {article.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {formatDate(article.date)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-user-star-line"></i>
                导演：{article.director}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatViews(article.views)} 阅读
              </span>
            </div>

            <div className="mt-6 rounded-lg border-l-4 border-accent-500 bg-accent-50/70 p-5">
              <p className="text-sm leading-relaxed text-foreground-800">{article.synopsis}</p>
            </div>

            <div className="mt-8">
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">详细剧情解说</h2>
              <div className="mt-4 space-y-4 text-sm leading-loose text-foreground-700 md:text-base">
                {(expanded ? paragraphs : collapsedText).map((para, index) => (
                  <p key={index}>{para}</p>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="mt-5 inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
              >
                {expanded ? '收起全文' : '展开全文'}
                <i className={expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
              </button>
            </div>

            <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">所属分类</h2>
              <div className="mt-3 flex flex-wrap gap-3">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-folder-line"></i>
                  {category?.name ?? '分类'}
                </Link>
                <Link
                  to="/news"
                  className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-newspaper-line"></i>
                  全部资讯
                </Link>
              </div>
            </div>
          </div>
        </div>

        {relatedArticles.length > 0 && (
          <section className="mt-14 border-t border-background-200 pt-10">
            <h2 className="mb-6 font-heading text-lg font-bold text-foreground-950 md:text-xl">相关推荐</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {relatedArticles.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </section>
        )}
      </article>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 animate-fade-up rounded-md bg-foreground-950 px-5 py-3 text-sm font-medium text-background-50">
          {toast}
        </div>
      )}
    </>
  );
}